import { GeneralPopup, PageHeader, PageWrapper } from 'components/partials';
import { AutoComplete, Button, EmptyState, GroupContainer, Icon, Spinner, Table, Tabs, WarningMessage } from 'components/widgets';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import posed, { PoseGroup } from 'react-pose';
import { IconSizes } from '../components/foundations/Icon';
import DownloadIcon from '../resources/icons/download.svg';
import { useAsync } from '../utils/customHooks';
import { convertToCsv, download } from '../utils/export';
import MemberBox from './components/MemberBox';
import WizardWarningMessage, { MessageTypes } from './components/WizardWarningMessage';
import AgencyDialog from './containers/AgencyDialog';
import ConfigurationRequiredDialog from './containers/ConfigurationRequiredDialog';
import ManagePermissionsPage from './containers/ManagePermissionsPage';
import TeamManagementShelf from './containers/TeamManagementShelf';
import TeamManagementAPI from './service';
import css from './styles.css';
import { getTableColumns } from './tableUtils';
import { MembersByGroupSchema, MembersGroupsSchema } from './types';
import { getSortedMembers, MEMBER_GROUP_TYPES, MEMBER_TYPES, trackTeamManagementMixpanelEvent } from './utils';

const api = new TeamManagementAPI();

const AnimationItem = posed.div({
    enter: {
        opacity: 1,
        y: 0,
        delay: ({ i }) => i * 50,
    },
    exit: { opacity: 0, y: 20 },
});

const getExportTeamFileName = () => `singular_team_${new Date().toISOString()}.csv`;

function Page({
    memberTypes = [],
    activeMemberType = MEMBER_TYPES.USER,
    onActiveMemberTypeChange = () => {},
    membersByGroup = {},
    membersGroups = [],
    pageLoaded = false,
    page = { show: false },
    onDeleteMember = () => {},
    onDeleteBtnClicked = () => {},
    onCreateNewMember = () => {},
    onManagePermissions = () => {},
    onResendInvitation = () => {},
    translate = () => {},
}) {
    const [memberToDelete, setMemberToDelete] = useState(null);
    const [search, setSearch] = useState('');
    const [exportTeamMembersResponse, runExportTeamMembers] = useAsync();
    const [usersSortConfig, setUsersSortConfig] = useState({});
    const [agenciesSortConfig, setAgenciesSortConfig] = useState({
        name: 'name',
        direction: 'down',
    });
    const exportStartTime = useRef(null);

    const {
        data: exportTeamMembers,
        isLoading: isLoadingExportAll,
        error: exportTeamMembersError,
    } = exportTeamMembersResponse;

    const handleDeleteClicked = member => {
        onDeleteBtnClicked();
        setMemberToDelete(member);
    };

    const onDeleteApproved = () => {
        onDeleteMember(memberToDelete);
        setMemberToDelete(null);
    };

    const onDeleteRejected = () => setMemberToDelete(null);

    const getActionButtons = (group, member) => {
        if (group === MEMBER_GROUP_TYPES.active) {
            return [
                {
                    buttonName: <Translate id="STATIC.PAGES.TEAM_MEMBERS.MANAGE_PERMISSIONS" />,
                    onClick: () => onManagePermissions(member),
                },
            ];
        }

        if (group === MEMBER_GROUP_TYPES.pending) {
            return [
                {
                    buttonName: member.inProgress ? (
                        <Translate id="STATIC.PAGES.TEAM_MEMBERS.SENDING_INVITE" />
                    ) : (
                        <Translate id="STATIC.PAGES.TEAM_MEMBERS.RESEND_INVITE_REACT" />
                    ),
                    onClick: () => onResendInvitation(member),
                    inProgress: member.inProgress,
                    disabled: member.disabled,
                    disabledTooltip: member.disabledTootlip,
                },
            ];
        }

        return [];
    };

    const onExportClick = () => runExportTeamMembers(api.exportTeamMembers());

    const onChangeSort = name => {
        const setSortConfig = activeMemberType === MEMBER_TYPES.USER ? setUsersSortConfig : setAgenciesSortConfig;
        setSortConfig(prev => {
            let direction = 'down';
            if (prev.name === name) {
                if (prev.direction === null) direction = 'down';
                else if (prev.direction === 'down') direction = 'up';
                else direction = null;
            }
            return {
                name,
                direction,
            };
        });
    };

    const tableColumns = getTableColumns(
        activeMemberType === MEMBER_TYPES.USER ? usersSortConfig : agenciesSortConfig,
        onChangeSort,
        onManagePermissions,
        handleDeleteClicked,
        pageLoaded,
        translate
    );

    useEffect(() => {
        if (isLoadingExportAll) {
            exportStartTime.current = performance.now();
        }

        if (exportTeamMembers) {
            const fileName = getExportTeamFileName();
            const csvData = convertToCsv(exportTeamMembers.fields, exportTeamMembers.data);
            download(fileName, csvData);

            trackTeamManagementMixpanelEvent('Export Team Button Clicked', {
                durationInMilliseconds: performance.now() - exportStartTime.current,
            });
        }
    }, [exportTeamMembersResponse]);

    const addMemberButton = (
        <Button
            type="primary"
            level="level1"
            className={css.newMemberButton}
            textClassName={css.newMemberButtonText}
            onClick={onCreateNewMember}
            disabled={!pageLoaded}
        >
            <Icon name="add-fill" variant={pageLoaded ? 'white' : 'light'} size={IconSizes.SMALL} />
            <Translate id={`STATIC.BUTTONS.${activeMemberType}_CREATE_NEW_MEMBER`} />
        </Button>
    );

    const renderInfoMessages = infoMessages =>
        infoMessages.map(infoMessage => (
            <WarningMessage
                key={infoMessage.message}
                type="success"
                className={css.infoMessage}
                showIcon
                showTypeIcon
                {...infoMessage}
            />
        ));

    return (
        <PageWrapper className={css.pageWrapper} containerWrapperClass={css.containerWrapper} disablePadding>
            {page.show ? (
                <ManagePermissionsPage />
            ) : (
                <div className={css.mainContainer}>
                    <div className={css.headerContainer}>
                        <PageHeader
                            text="STATIC.MANAGE_TEAM"
                            leftComponents={[]}
                            rightComponent={[]}
                            subText={{
                                faqLink:
                                    'https://support.singular.net/hc/en-us/articles/360020835952-Team-Management-FAQ',
                            }}
                        />
                        {memberTypes?.length > 1 && (
                            <Tabs
                                containerStyle={{ marginTop: 10 }}
                                selected={activeMemberType}
                                onClick={onActiveMemberTypeChange}
                                tabs={memberTypes.map(({ type, count }) => ({
                                    id: type,
                                    label: `STATIC.PAGES.TEAM_MEMBERS.TAB_LABEL_${type.toUpperCase()}`,
                                    labelData: {
                                        count: pageLoaded ? `(${count})` : '',
                                    },
                                }))}
                            />
                        )}
                        <div className={css.filterRow}>
                            <AutoComplete
                                placeholder={translate(`STATIC.PAGES.TEAM_MEMBERS.PLACEHOLDER.${activeMemberType.toUpperCase()}_SEARCH`)}
                                containerStyle={{ flexBasis: '500px', marginRight: '20px' }}
                                selectOptions={{
                                    options: [],
                                    isSearchable: true,
                                    inputValue: search,
                                    noOptionsMessage: () => null,
                                }}
                                onInputChange={value => setSearch(value.toLowerCase())}
                            />
                            <div className={css.buttonsContainer}>
                                {activeMemberType === MEMBER_TYPES.USER && (
                                    <Button
                                        type="secondary"
                                        className={css.exportTeamMembersButton}
                                        showSpinner={!!isLoadingExportAll}
                                        Icon={DownloadIcon}
                                        onClick={onExportClick}
                                        iconClass={css.downloadIcon}
                                        disabled={!pageLoaded}
                                    >
                                        <Translate id="STATIC.BUTTONS.EXPORT" />
                                    </Button>
                                )}
                                {addMemberButton}
                            </div>
                        </div>
                        {exportTeamMembersError && (
                            <WizardWarningMessage
                                type={MessageTypes.ERROR}
                                show
                                duration={1000}
                                message="STATIC.PAGES.TEAM_MEMBERS.EXPORT_ERROR"
                                className={css.exportTeamMembersError}
                            />
                        )}
                    </div>
                    <div className={css.membersContainer}>
                        {!pageLoaded &&
                            (activeMemberType === MEMBER_TYPES.USER ? (
                                <Spinner show />
                            ) : (
                                <Table data={Array(4).fill({})} metadata={tableColumns} />
                            ))}
                        {pageLoaded &&
                            membersGroups.map(({ name: groupName, displayName: groupDisplayName, infoMessages }) => {
                                const members = membersByGroup[groupName];
                                const filterMembers = getSortedMembers(
                                    members.filter(member => member.searchText.includes(search)),
                                    activeMemberType === MEMBER_TYPES.USER ? usersSortConfig : agenciesSortConfig
                                );
                                const translatedGroup = translate(groupDisplayName);
                                return activeMemberType === MEMBER_TYPES.USER ? (
                                    <GroupContainer
                                        key={`group_${groupName}`}
                                        title={`${translatedGroup} (${members.length})`}
                                        defaultOpen
                                        className={css.groupContainer}
                                    >
                                        <div>
                                            {renderInfoMessages(infoMessages)}
                                            <PoseGroup animateOnMount>
                                                {filterMembers.map((member, index) => (
                                                    <AnimationItem key={`groupItem_${member.id}`} i={index}>
                                                        <MemberBox
                                                            {...member}
                                                            status={groupName}
                                                            style={{ marginTop: index > 0 ? 10 : 0 }}
                                                            highlightedText={search}
                                                            onDelete={() => handleDeleteClicked(member)}
                                                            actionButtons={getActionButtons(groupName, member)}
                                                        />
                                                    </AnimationItem>
                                                ))}
                                            </PoseGroup>
                                        </div>
                                    </GroupContainer>
                                ) : (
                                    <>
                                        {renderInfoMessages(infoMessages)}
                                        <Table
                                            data={filterMembers}
                                            metadata={tableColumns}
                                            virtualized
                                            emptyState={
                                                members.length === 0 ? (
                                                    <EmptyState
                                                        customIcon={
                                                            <Icon
                                                                name="briefcase"
                                                                variant="light"
                                                                interactive={false}
                                                            />
                                                        }
                                                        containerClass={css.emptyStateContainer}
                                                        textContainerClass={css.emptyStateContainer}
                                                        headerClassName={css.emptyStateText}
                                                        subHeaderClassName={css.emptyStateText}
                                                        header="STATIC.PAGES.TEAM_MEMBERS.EMPTY_STATE.HEADER_AGENCY"
                                                        headerStyle={{ fontSize: 20, fontWeight: 600 }}
                                                        subHeader="STATIC.PAGES.TEAM_MEMBERS.EMPTY_STATE.SUBHEADER_AGENCY"
                                                    >
                                                        {addMemberButton}
                                                    </EmptyState>
                                                ) : (
                                                    <Translate id="STATIC.PAGES.TEAM_MEMBERS.EMPTY_STATE.NO_AGENCIES" />
                                                )
                                            }
                                        />
                                    </>
                                );
                            })}
                    </div>
                </div>
            )}
            <GeneralPopup
                open={!!memberToDelete}
                type="delete"
                title="STATIC.PAGES.TEAM_MEMBERS.DELETE_MODAL.TITLE"
                text="STATIC.PAGES.TEAM_MEMBERS.DELETE_MODAL.MESSAGE"
                acceptText="STATIC.PAGES.TEAM_MEMBERS.DELETE_MODAL.ACCEPT_BUTTON_TEXT"
                rejectText="STATIC.PAGES.TEAM_MEMBERS.DELETE_MODAL.REJECT_BUTTON_TEXT"
                onAccept={onDeleteApproved}
                onReject={onDeleteRejected}
            />
            <TeamManagementShelf />
            <AgencyDialog />
            <ConfigurationRequiredDialog />
        </PageWrapper>
    );
}

Page.propTypes = {
    pageLoaded: PropTypes.bool,
    memberTypes: PropTypes.arrayOf(
        PropTypes.shape({
            type: PropTypes.string,
            count: PropTypes.number,
        })
    ),
    membersByGroup: MembersByGroupSchema.isRequired,
    membersGroups: MembersGroupsSchema.isRequired,
    activeMemberType: PropTypes.string,
    onActiveMemberTypeChange: PropTypes.func,
    onDeleteMember: PropTypes.func,
    onDeleteBtnClicked: PropTypes.func,
    onManagePermissions: PropTypes.func,
    onResendInvitation: PropTypes.func,
    onCreateNewMember: PropTypes.func,
    translate: PropTypes.func,
    page: PropTypes.shape({
        show: PropTypes.bool,
        loading: PropTypes.bool,
        selectedTabIndex: PropTypes.number,
        stepValidationStatus: PropTypes.shape({
            isValid: PropTypes.bool,
            title: PropTypes.string,
        }),
    }),
};

export default withLocalize(Page);
